import type { GraphPathLookupParametersBase } from '../api/GraphPathLookupParameters';
import type { DeviceGroupAttribute } from '../graphSettings/index';
import type { CloudConstructKind, CloudConstructVendor } from '../nodes/types';
import type { Filters } from '../table/filters/filters';
import { makeTypeGuardByPropertyValue } from '../types/makeTypeGuardByPropertyValue';

export type OasTableColumnLinkGraphTopology = {
  type: 'topology';
  groupBy: DeviceGroupAttribute;
  attributeField: string;
  stpInstancesChart?: {
    rootIdColumn: string;
    vlanIdColumn: string;
  };
};

export const isOasTableColumnLinkGraphTopology = makeTypeGuardByPropertyValue<
  OasTableColumnLink,
  OasTableColumnLinkGraphTopology
>()('type', 'topology');

export type OasTableColumnLinkGraphPathLookup = GraphPathLookupParametersBase &
  (
    | {
        pathLookupType: 'unicast';
        startingPointColumn: string;
        destinationPointColumn: string;
      }
    | {
        pathLookupType: 'multicast';
        sourceColumn: string;
        groupColumn: string;
        receiverColumn?: string;
      }
    | {
        pathLookupType: 'hostToDefaultGW';
        startingPointColumn: string;
      }
  );

export const isOasTableColumnLinkGraphPathLookup = makeTypeGuardByPropertyValue<
  OasTableColumnLink,
  OasTableColumnLinkGraphPathLookup
>()('type', 'pathLookup');

export type OasTableColumnLinkTable = {
  type: 'table';
  /** webPath of target table */
  target: string;
  filter: Filters;
  /**
   * Show the trailing link glyph on the cell. Defaults to `true`.
   *
   * Set `false` for tables whose cells are predominantly links, where a glyph on
   * every one is noise rather than an affordance.
   */
  icon?: boolean;
};

export const isOasTableColumnLinkTable = makeTypeGuardByPropertyValue<
  OasTableColumnLink,
  OasTableColumnLinkTable
>()('type', 'table');

export type CloudSecurityTreeDirection = 'inbound' | 'outbound';

/**
 * Opens the cloud security policy tree in place (overlay modal), analogous to
 * the device `security` link. Attached to a `number` column whose value is the
 * rule count; `resourceNameField` identifies the policy and `direction` its
 * rule set.
 */
export type OasTableColumnLinkCloudSecurityTree = {
  type: 'cloudSecurityTree';
  resourceNameField: string;
  direction: CloudSecurityTreeDirection;
};

export const isOasTableColumnLinkCloudSecurityTree =
  makeTypeGuardByPropertyValue<
    OasTableColumnLink,
    OasTableColumnLinkCloudSecurityTree
  >()('type', 'cloudSecurityTree');

/**
 * Opens the Device Explorer for a cloud construct identified by the row.
 *
 * Like `cloudSecurityTree` this one does not navigate away: the Explorer is
 * in-page state, so the link stays on the current route and carries the
 * construct in `state` (see `createCloudConstructLink`).
 */
export type OasTableColumnLinkCloudConstruct = {
  type: 'cloudConstruct';
  /** Which cloud construct kind the Explorer opens. */
  constructKind: CloudConstructKind;
  /**
   * Row field holding the construct id. Defaults to the column's own value,
   * which is the common case (id columns); set it when a different column
   * carries the id (e.g. a `name` column opening the construct by `subnetId`).
   */
  idField?: string;
  /**
   * Cloud provider used to resolve the construct node type. Defaults to the
   * row's `cloudProvider` column; state it explicitly on vendor-specific
   * tables that have no such column.
   */
  vendor?: CloudConstructVendor;
  /** Row field holding the display name (falls back to the construct id). */
  nameField?: string;
  /**
   * Values passed to the Explorer as `extra`, driving its detail tab and
   * sub-table filters. Maps the Explorer's `extra` key to the row column
   * holding it, e.g. `{ cloudNetworkId: 'vpcId' }`.
   */
  extraFields?: Record<string, string>;
  /**
   * Optional value gate: the cell links only when the row's `field` column
   * equals `equals`. Used e.g. on Public IPs so `resourceId` links only when
   * `resourceType === 'networkInterface'`.
   */
  linkWhen?: { field: string; equals: string };
};

export const isOasTableColumnLinkCloudConstruct = makeTypeGuardByPropertyValue<
  OasTableColumnLink,
  OasTableColumnLinkCloudConstruct
>()('type', 'cloudConstruct');

/** Key under which a `cloudConstruct` link's payload travels in router state. */
export const CLOUD_CONSTRUCT_LINK_STATE_KEY = 'cloudConstructExplorer';

/** Payload carried under {@link CLOUD_CONSTRUCT_LINK_STATE_KEY}. */
export interface CloudConstructLinkPayload {
  constructKind: CloudConstructKind;
  /** Construct id, used as the Device Explorer entity sn. */
  constructId: string;
  vendor: string;
  /** Display name; falls back to the construct id. */
  name: string | null;
  /** Values driving the Explorer's detail tab and sub-table filters. */
  extra: Record<string, string>;
}

/** Router state produced by a `cloudConstruct` link. */
export interface CloudConstructLinkState {
  [CLOUD_CONSTRUCT_LINK_STATE_KEY]: CloudConstructLinkPayload;
}

export type OasTableColumnLink =
  | OasTableColumnLinkGraphPathLookup
  | OasTableColumnLinkGraphTopology
  | OasTableColumnLinkTable
  | OasTableColumnLinkCloudSecurityTree
  | OasTableColumnLinkCloudConstruct;
